import liabilityService from '@/services/liabilityService';
import { INSTALLMENT_LOAN_TYPES, SIMPLE_LIABILITY_TYPES } from '@/composables/useLoanCalculator';

export default {
  namespaced: true,
  
  state: {
    liabilities: [],
    count: 0,
    loading: false,
    error: null,
    // Payments state
    payments: {},  // Map of liabilityId -> payments array
    paymentsLoading: false,
    paymentsError: null
  },
  
  getters: {
    allLiabilities: state => state.liabilities,
    liabilityCount: state => state.count,
    isLoading: state => state.loading,
    error: state => state.error,
    
    // Get liabilities by type
    getLiabilitiesByType: state => type => {
      return state.liabilities.filter(l => l.type === type);
    },
    
    // Get installment loans only
    installmentLoans: state => {
      return state.liabilities.filter(l => INSTALLMENT_LOAN_TYPES.includes(l.type));
    },
    
    // Get simple liabilities
    simpleLiabilities: state => {
      return state.liabilities.filter(l => SIMPLE_LIABILITY_TYPES.includes(l.type));
    },
    
    // Calculate total debt
    totalDebt: state => {
      return state.liabilities.reduce((sum, liability) => {
        return sum + (liability.currentBalance || 0);
      }, 0);
    },
    
    // Calculate total monthly payments (from installment loans)
    totalMonthlyPayments: state => {
      return state.liabilities
        .filter(l => INSTALLMENT_LOAN_TYPES.includes(l.type))
        .reduce((sum, liability) => {
          return sum + (liability.monthlyPayment || 0);
        }, 0);
    },
    
    // Get payments for a specific liability
    getPaymentsForLiability: state => liabilityId => {
      return state.payments[liabilityId] || [];
    },
    
    // Get total paid amount for a liability
    getTotalPaidForLiability: state => liabilityId => {
      const payments = state.payments[liabilityId] || [];
      return payments.reduce((sum, p) => sum + (p.amount || 0), 0);
    },
    
    // Payments loading state
    isPaymentsLoading: state => state.paymentsLoading,
    paymentsError: state => state.paymentsError
  },
  
  mutations: {
    SET_LIABILITIES(state, liabilities) {
      state.liabilities = liabilities;
    },
    SET_COUNT(state, count) {
      state.count = count;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    ADD_LIABILITY(state, liability) {
      state.liabilities.push(liability);
      state.count++;
    },
    UPDATE_LIABILITY(state, updatedLiability) {
      const index = state.liabilities.findIndex(l => l.id === updatedLiability.id);
      if (index !== -1) {
        state.liabilities.splice(index, 1, updatedLiability);
      }
    },
    DELETE_LIABILITY(state, liabilityId) {
      state.liabilities = state.liabilities.filter(l => l.id !== liabilityId);
      state.count = Math.max(0, state.count - 1);
      // Also clear payments for this liability
      delete state.payments[liabilityId];
    },
    
    // Payment mutations
    SET_PAYMENTS_LOADING(state, loading) {
      state.paymentsLoading = loading;
    },
    SET_PAYMENTS_ERROR(state, error) {
      state.paymentsError = error;
    },
    SET_PAYMENTS_FOR_LIABILITY(state, { liabilityId, payments }) {
      state.payments = { ...state.payments, [liabilityId]: payments };
    },
    ADD_PAYMENT(state, payment) {
      const liabilityId = payment.liabilityId;
      if (!state.payments[liabilityId]) {
        state.payments[liabilityId] = [];
      }
      state.payments[liabilityId].push(payment);
      // Sort by date (newest first)
      state.payments[liabilityId].sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
    },
    UPDATE_PAYMENT(state, updatedPayment) {
      const liabilityId = updatedPayment.liabilityId;
      if (state.payments[liabilityId]) {
        const index = state.payments[liabilityId].findIndex(p => p.id === updatedPayment.id);
        if (index !== -1) {
          state.payments[liabilityId].splice(index, 1, updatedPayment);
          // Re-sort by date
          state.payments[liabilityId].sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
        }
      }
    },
    DELETE_PAYMENT(state, { paymentId, liabilityId }) {
      if (state.payments[liabilityId]) {
        state.payments[liabilityId] = state.payments[liabilityId].filter(p => p.id !== paymentId);
      }
    }
  },
  
  actions: {
    /**
     * Fetch all liabilities for the current user
     */
    async fetchLiabilities({ commit, rootState }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const userEmail = rootState.auth.user?.email;
        if (!userEmail) {
          throw new Error('User not authenticated');
        }
        const response = await liabilityService.getAllLiabilities(userEmail);
        commit('SET_LIABILITIES', response.data);
        commit('SET_COUNT', response.data.length);
      } catch (error) {
        console.error('Error fetching liabilities:', error);
        commit('SET_ERROR', error.message || 'Failed to fetch liabilities');
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch liability count
     */
    async fetchCount({ commit, rootState }) {
      try {
        const userEmail = rootState.auth.user?.email;
        if (!userEmail) return;
        const response = await liabilityService.getLiabilityCount(userEmail);
        commit('SET_COUNT', response.data);
      } catch (error) {
        console.error('Error fetching liability count:', error);
      }
    },
    
    /**
     * Create a new liability
     */
    async createLiability({ commit, rootState, dispatch }, liability) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const liabilityData = {
          ...liability,
          userId: rootState.auth.user?.email
        };
        const response = await liabilityService.createLiability(liabilityData);
        commit('ADD_LIABILITY', response.data);
        dispatch('ui/showToast', { message: 'Liability created successfully', type: 'success' }, { root: true });
        return response.data;
      } catch (error) {
        console.error('Error creating liability:', error);
        const status = error.response?.status;
        const errorType = error.response?.data?.error;
        const message = error.response?.data?.message || 'Failed to create liability';
        
        // Check if this is a quota exceeded error - let the component handle it
        const isQuotaError = (status === 402 || status === 403) && 
          (errorType === 'QuotaExceededException' || message.toLowerCase().includes('limit'));
        
        commit('SET_ERROR', message);
        
        // Only show toast for non-quota errors (quota errors are handled by the modal)
        if (!isQuotaError) {
          dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        }
        
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Update an existing liability
     */
    async updateLiability({ commit, rootState, dispatch }, liability) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const liabilityData = {
          ...liability,
          userId: rootState.auth.user?.email
        };
        const response = await liabilityService.updateLiability(liability.id, liabilityData);
        commit('UPDATE_LIABILITY', response.data);
        dispatch('ui/showToast', { message: 'Liability updated successfully', type: 'success' }, { root: true });
        return response.data;
      } catch (error) {
        console.error('Error updating liability:', error);
        const message = error.response?.data?.message || 'Failed to update liability';
        commit('SET_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Delete a liability
     */
    async deleteLiability({ commit, dispatch }, liabilityId) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        await liabilityService.deleteLiability(liabilityId);
        commit('DELETE_LIABILITY', liabilityId);
        dispatch('ui/showToast', { message: 'Liability deleted successfully', type: 'success' }, { root: true });
      } catch (error) {
        console.error('Error deleting liability:', error);
        const message = error.response?.data?.message || 'Failed to delete liability';
        commit('SET_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Recalculate amortization for a liability
     */
    async recalculateLiability({ commit, dispatch }, liabilityId) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await liabilityService.recalculateLiability(liabilityId);
        commit('UPDATE_LIABILITY', response.data);
        dispatch('ui/showToast', { message: 'Liability recalculated successfully', type: 'success' }, { root: true });
        return response.data;
      } catch (error) {
        console.error('Error recalculating liability:', error);
        const message = error.response?.data?.message || 'Failed to recalculate liability';
        commit('SET_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ==================== Payment Actions ====================

    /**
     * Fetch payments for a specific liability
     */
    async fetchPaymentsForLiability({ commit }, liabilityId) {
      commit('SET_PAYMENTS_LOADING', true);
      commit('SET_PAYMENTS_ERROR', null);
      try {
        const response = await liabilityService.getPaymentsForLiability(liabilityId);
        commit('SET_PAYMENTS_FOR_LIABILITY', { liabilityId, payments: response.data });
        return response.data;
      } catch (error) {
        console.error('Error fetching payments:', error);
        commit('SET_PAYMENTS_ERROR', error.message || 'Failed to fetch payments');
        throw error;
      } finally {
        commit('SET_PAYMENTS_LOADING', false);
      }
    },

    /**
     * Create a new payment
     */
    async createPayment({ commit, dispatch }, payment) {
      commit('SET_PAYMENTS_LOADING', true);
      commit('SET_PAYMENTS_ERROR', null);
      try {
        const response = await liabilityService.createPayment(payment);
        commit('ADD_PAYMENT', response.data);
        dispatch('ui/showToast', { message: 'Payment recorded successfully', type: 'success' }, { root: true });
        // Refresh liability to get updated balance
        await dispatch('refreshLiability', payment.liabilityId);
        return response.data;
      } catch (error) {
        console.error('Error creating payment:', error);
        const message = error.response?.data?.message || 'Failed to record payment';
        commit('SET_PAYMENTS_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_PAYMENTS_LOADING', false);
      }
    },

    /**
     * Update an existing payment
     */
    async updatePayment({ commit, dispatch }, { paymentId, payment }) {
      commit('SET_PAYMENTS_LOADING', true);
      commit('SET_PAYMENTS_ERROR', null);
      try {
        const response = await liabilityService.updatePayment(paymentId, payment);
        commit('UPDATE_PAYMENT', response.data);
        dispatch('ui/showToast', { message: 'Payment updated successfully', type: 'success' }, { root: true });
        // Refresh liability to get updated balance
        await dispatch('refreshLiability', payment.liabilityId);
        return response.data;
      } catch (error) {
        console.error('Error updating payment:', error);
        const message = error.response?.data?.message || 'Failed to update payment';
        commit('SET_PAYMENTS_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_PAYMENTS_LOADING', false);
      }
    },

    /**
     * Delete a payment
     */
    async deletePayment({ commit, dispatch }, { paymentId, liabilityId }) {
      commit('SET_PAYMENTS_LOADING', true);
      commit('SET_PAYMENTS_ERROR', null);
      try {
        await liabilityService.deletePayment(paymentId);
        commit('DELETE_PAYMENT', { paymentId, liabilityId });
        dispatch('ui/showToast', { message: 'Payment deleted successfully', type: 'success' }, { root: true });
        // Refresh liability to get updated balance
        await dispatch('refreshLiability', liabilityId);
      } catch (error) {
        console.error('Error deleting payment:', error);
        const message = error.response?.data?.message || 'Failed to delete payment';
        commit('SET_PAYMENTS_ERROR', message);
        dispatch('ui/showToast', { message, type: 'error' }, { root: true });
        throw error;
      } finally {
        commit('SET_PAYMENTS_LOADING', false);
      }
    },

    /**
     * Refresh a single liability (to get updated balance after payment changes)
     */
    async refreshLiability({ commit }, liabilityId) {
      try {
        const response = await liabilityService.getLiability(liabilityId);
        commit('UPDATE_LIABILITY', response.data);
        return response.data;
      } catch (error) {
        console.error('Error refreshing liability:', error);
      }
    }
  }
};

